import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Button,
    CircularProgress,
    Skeleton
} from '@mui/material';
import {
    Visibility as ViewIcon
} from '@mui/icons-material';
import { transformToBTC } from '../../../../utils/helpers';

const UserTransactionsTab = ({
    transactions,
    loading,
    hasMore,
    loadingMore,
    onLoadMore
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const formatDate = (timestamp) => {
        return new Date(timestamp).toLocaleString();
    };

    const TransactionRowSkeleton = () => (
        <TableRow>
            <TableCell>
                <Skeleton variant="rounded" width={60} height={24} />
            </TableCell>
            <TableCell>
                <Skeleton variant="text" width={120} height={20} />
            </TableCell>
            <TableCell>
                <Skeleton variant="text" width={150} height={20} />
            </TableCell>
            <TableCell>
                <Skeleton variant="text" width={140} height={20} />
            </TableCell>
            <TableCell>
                <Skeleton variant="circular" width={32} height={32} />
            </TableCell>
        </TableRow>
    );

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                {loading ? (
                    <Skeleton variant="text" width={200} height={28} />
                ) : (
                    t('user.recentTransactions')
                )}
            </Typography>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('user.type')}</TableCell>
                            <TableCell>{t('user.amount')}</TableCell>
                            <TableCell>{t('user.address')}</TableCell>
                            <TableCell>{t('user.time')}</TableCell>
                            <TableCell>{t('user.actions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            // Show skeleton rows while loading
                            Array.from({ length: 5 }).map((_, index) => (
                                <TransactionRowSkeleton key={index} />
                            ))
                        ) : (
                            transactions.map((tx, index) => (
                                <TableRow key={index} hover>
                                    <TableCell>
                                        <Chip
                                            label={tx.type}
                                            color={tx.type === 'deposit' ? 'success' : 'warning'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                            {transformToBTC(tx.amount)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {tx.type === 'deposit' && (
                                            tx.senderAddresses.map((address, addrIndex) => (
                                                <Typography
                                                    key={addrIndex + address}
                                                    variant="body2"
                                                    sx={{
                                                        fontFamily: 'monospace',
                                                        fontSize: '0.8rem',
                                                        cursor: 'pointer',
                                                        '&:hover': { textDecoration: 'underline' }
                                                    }}
                                                    onClick={() => window.open(`/explorer/address/${ address }`)}
                                                >
                                                    {address.slice(0, 12) + '...'}
                                                </Typography>
                                            ))
                                        )}
                                        {tx.type === 'withdrawal' && (
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontFamily: 'monospace',
                                                    fontSize: '0.8rem',
                                                    cursor: 'pointer',
                                                    '&:hover': { textDecoration: 'underline' }
                                                }}
                                                onClick={() => window.open(`/explorer/address/${ tx.withdrawAddress }`)}
                                            >
                                                {tx.withdrawAddress.slice(0, 12) + '...'}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary">
                                            {formatDate(tx.time)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <IconButton
                                            size="small"
                                            onClick={() => window.open(`/explorer/transaction/${ tx.txId }`)}
                                        >
                                            <ViewIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {hasMore && !loading && (
                <Box sx={{ textAlign: 'center', mt: 2 }}>
                    <Button
                        variant="outlined"
                        onClick={onLoadMore}
                        disabled={loadingMore}
                    >
                        {loadingMore ? <CircularProgress size={20} /> : t('user.loadMore')}
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default UserTransactionsTab;
